import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { mpTrack } from "@/lib/metaPixel";

const AuthContext = createContext(null);

/** Auto-detect UI language on first ever launch.
 *  Product rule: Polish browsers land on PL; everyone else on EN (US).
 *  Manual toggle in Settings overrides this by writing localStorage +
 *  PATCH /auth/lang. */
function detectBrowserLang() {
  try {
    const candidates = [
      navigator.language,
      ...(navigator.languages || []),
    ].filter(Boolean).map((l) => String(l).toLowerCase());
    if (candidates.some((l) => l.startsWith("pl"))) return "pl";
    return "en";
  } catch {
    return "en";
  }
}

/** Read the stored language, or pick one via auto-detection and persist it.
 *  Runs synchronously inside useState initialiser so the very first paint
 *  is already in the correct language. */
function initialLang() {
  try {
    const stored = localStorage.getItem("leo_lang");
    if (stored === "pl" || stored === "en") return stored;
    const detected = detectBrowserLang();
    localStorage.setItem("leo_lang", detected);
    return detected;
  } catch {
    return "en";
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [access, setAccess] = useState(null);
  const [lang, setLangState] = useState(initialLang);

  // Browsers decide whether to auto-translate from <html lang>; keep it
  // truthful so Polish chat text is never machine-"translated" in place.
  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const refreshAccess = useCallback(async () => {
    try {
      const r = await api.get("/access");
      setAccess(r.data);
    } catch {
      setAccess(null);
    }
  }, []);

  const applyUserLang = useCallback((u) => {
    if (u?.lang && (u.lang === "pl" || u.lang === "en")) {
      setLangState(u.lang);
      localStorage.setItem("leo_lang", u.lang);
    }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const r = await api.get("/auth/me");
      setUser(r.data);
      applyUserLang(r.data);
      await refreshAccess();
    } catch {
      setUser(null);
      setAccess(null);
    } finally {
      setLoading(false);
    }
  }, [refreshAccess, applyUserLang]);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const loginEmail = async (email, password) => {
    const r = await api.post("/auth/login", { email, password });
    localStorage.setItem("leo_token", r.data.token);
    setUser(r.data.user);
    applyUserLang(r.data.user);
    await refreshAccess();
    return r.data.user;
  };

  const registerEmail = async (email, password, name) => {
    // Pass the browser-detected `lang` so the backend persists the correct
    // initial language on the user document. Backend will also fall back to
    // Accept-Language if this field is missing.
    const r = await api.post("/auth/register", { email, password, name, lang });
    localStorage.setItem("leo_token", r.data.token);
    setUser(r.data.user);
    applyUserLang(r.data.user);
    await refreshAccess();
    // Meta Pixel: fire CompleteRegistration only after a genuine new account
    // creation. `content_name` is the auth method; no personal data is sent.
    mpTrack("CompleteRegistration", { content_name: "email" });
    return r.data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("leo_token");
    setUser(null);
    setAccess(null);
  };

  const setLang = useCallback(async (next) => {
    if (next !== "pl" && next !== "en" && next !== "ja") return;
    setLangState(next);
    localStorage.setItem("leo_lang", next);
    if (user) {
      try {
        await api.patch("/auth/lang", { lang: next });
        setUser(u => u ? { ...u, lang: next } : u);
      } catch {}
    }
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, setUser, loading, access, refreshAccess, refreshUser: checkAuth, loginEmail, registerEmail, logout, lang, setLang }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
